import InfiniteScroll from 'react-infinite-scroll-component';
import isEmpty from 'lodash/isEmpty';
import { Dispatch, SetStateAction, useState } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import HolderPaymentsTabFilters from '@/modules/issuer-dashboard/components/HolderPaymentsTabFilters.tsx';
import { IssuerPaymentItem } from '@/modules/issuer-dashboard/components/items/IssuerPaymentItem.tsx';
import { usePaymentsListQuery } from '@/modules/issuer-dashboard/queries/payments.tsx';
import { EmptyListState, Spinner } from 'ui';
import { GetPaymentsListV1SrPaymentsGetParams, ShareholderExtendResponse } from 'api';

export type paymentsFilters = Pick<
  GetPaymentsListV1SrPaymentsGetParams,
  'status' | 'direction' | 'sorted_by' | 'requested_at_date_from' | 'requested_at_date_to'
>;

interface PaymentsTabProps {
  shareholder: ShareholderExtendResponse | null;
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
}

const PaymentsTab = ({ shareholder, isDeleted, setIsDeleted }: PaymentsTabProps) => {
  const [filters, setFilters] = useState<paymentsFilters>({});
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { data, isFetching, isLoading, fetchNextPage, hasNextPage } = usePaymentsListQuery(
    {
      page: 1,
      entity_id: shareholder?.entity_id,
      page_size: 15,
      ...filters,
      is_deleted: isDeleted,
    },
    !shareholder,
    shareholder?.register_id,
  );

  const payments = (data?.pages || []).flatMap(({ data }) => data);

  return (
    <div className="space-y-4">
      <HolderPaymentsTabFilters
        filters={filters}
        setFilters={setFilters}
        shareholder={shareholder}
        isDeleted={isDeleted}
        setIsDeleted={setIsDeleted}
        isFetching={isFetching}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      <EmptyListState
        title="No payments yet"
        description={`Payments for ${getRegisterTypeStrings('application', {
          plural: true,
        })} or for ${getRegisterTypeStrings('dividend', {
          plural: true,
        })} will appear here.`}
        isLoading={isLoading}
        isSearchUsed={!isEmpty(filters)}
        itemsCount={payments.length}
      />
      <InfiniteScroll
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        className="space-y-2"
        dataLength={payments.length}
        scrollableTarget="scrollable"
      >
        {payments.map(payment => (
          <IssuerPaymentItem
            key={payment.id}
            payment={payment}
            isDeleted={isDeleted}
            showArchiveButton={true}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default PaymentsTab;
